/**
 * A column of links that let somebody outside this workspace edit a row.
 *
 * The one field type whose configuration is a *view*: it points at a form view
 * in the same table, and every cell holds a signed link that opens that form
 * already filled in with the row it is on. The values are the backend's --
 * a uuid per row, which it turns into a url on the way out -- so there is
 * nothing to edit here but the form the links go to.
 */
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import type { ViewOf } from '@/api/types'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { useViews } from '../../../table/queries'
import { Setting } from './controls'
import type { SubForm } from './types'

/** The value the picker uses for "no form at all", which the API takes as null. */
const NONE = ''

export const FormViewEditRowForm: SubForm<'form_view_edit_row'> = ({
  properties,
  onChange,
  tableId,
}) => {
  const { t } = useTranslation()
  const id = useId()
  const views = useViews(tableId)

  // Only this table's, because the backend refuses any other -- and refuses a
  // grid view in this table with the same words, which is why the list is
  // narrowed rather than the refusal explained.
  const forms = (views.data ?? []).filter(
    (view): view is ViewOf<'form'> => view.type === 'form',
  )
  const chosen = properties.form_view_id ?? null
  const picked = forms.find((view) => view.id === chosen)

  // Nothing is decided until the views have arrived. A picker that showed "No
  // form" for a field that has one, and a warning that the form was deleted
  // beside it, would be saying two false things about every configured field
  // for as long as the request takes.
  if (views.isPending) {
    return (
      <Setting
        id={id}
        label={t('fieldConfig.formView')}
        hint={t('fieldConfig.formViewHint')}
      >
        <NativeSelect id={id} className="w-full" disabled value={NONE}>
          <NativeSelectOption value={NONE}>
            {t('fieldConfig.formViewLoading')}
          </NativeSelectOption>
        </NativeSelect>
      </Setting>
    )
  }

  return (
    <Setting
      id={id}
      label={t('fieldConfig.formView')}
      hint={t('fieldConfig.formViewHint')}
    >
      <NativeSelect
        id={id}
        className="w-full"
        value={chosen === null ? NONE : String(chosen)}
        onChange={(event) => {
          const value = event.target.value
          onChange({ form_view_id: value === NONE ? null : Number(value) })
        }}
      >
        <NativeSelectOption value={NONE}>
          {t('fieldConfig.formViewNone')}
        </NativeSelectOption>
        {forms.map((view) => (
          <NativeSelectOption key={view.id} value={view.id}>
            {view.name}
          </NativeSelectOption>
        ))}
      </NativeSelect>

      {/* Three things worth saying, and only one of them at a time. */}
      {chosen === null ? (
        <p className="text-sm text-muted-foreground">
          {forms.length === 0
            ? t('fieldConfig.formViewNoneExist')
            : t('fieldConfig.formViewEmptyColumn')}
        </p>
      ) : picked === undefined ? (
        // The picker cannot show a form that is gone, so without this the field
        // would look as though it pointed at whichever form happens to be first.
        <p className="text-sm text-destructive">{t('fieldConfig.formViewMissing')}</p>
      ) : (
        picked.public !== true && (
          // The link is built and stored either way: the backend never checks
          // that the form is shared, so a column of links that all refuse the
          // visitor is what an unshared form produces.
          <p className="text-sm text-destructive">{t('fieldConfig.formViewNotShared')}</p>
        )
      )}
    </Setting>
  )
}
